import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Filter, Globe, MapPin, Sparkles, Calendar } from 'lucide-react';
import api from '../lib/api';

const SCOPES = [
  { key: 'all',    label: 'All',    icon: Sparkles },
  { key: 'world',  label: 'World',  icon: Globe },
  { key: 'region', label: 'Region', icon: MapPin },
  { key: 'season', label: 'Season', icon: Star },
  { key: 'month',  label: 'Month',  icon: Calendar },
];

const EMOJI = { world: '🌎', region: '🍁', season: '🌸', month: '📅', park: '🌲' };

export default function Records() {
  const [scope, setScope] = useState('all');
  const [items, setItems] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = scope === 'all' ? '/records?limit=100' : `/records?scope=${scope}&limit=100`;
    Promise.all([
      api.get(url).then(r => r.data).catch(() => []),
      api.get('/records/mine?limit=50').then(r => r.data).catch(() => []),
    ]).then(([all, my]) => { setItems(all); setMine(my); setLoading(false); });
  }, [scope]);

  return (
    <div className="space-y-5" data-testid="records-root">
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-accent">Discovery Records</p>
        <h2 className="font-serif text-3xl">Historical firsts</h2>
        <p className="text-xs text-muted-foreground mt-1">World, regional, seasonal and monthly firsts — permanent achievements tied to explorers who logged them first.</p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Metric label="Your Records" value={mine.length} />
        <Metric label="World Firsts" value={mine.filter(r => r.scope === 'world').length} />
        <Metric label="Regional" value={mine.filter(r => r.scope === 'region').length} />
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="records-scope-row">
        <Filter className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
        {SCOPES.map(s => (
          <button
            key={s.key}
            onClick={() => setScope(s.key)}
            data-testid={`records-scope-${s.key}`}
            className={`flex-shrink-0 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
              scope === s.key ? 'border-accent/60 bg-accent/10 text-accent'
                : 'border-white/10 bg-card/50 text-muted-foreground hover:text-foreground'
            }`}
          >
            <s.icon className="w-3 h-3" />
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => <div key={i} className="h-16 rounded-2xl bg-card animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl bg-card border border-white/5 p-8 text-center">
          <Star className="w-8 h-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="font-serif text-lg">No records yet</p>
          <p className="text-xs text-muted-foreground mt-1">Be the first to discover a species and claim a permanent record.</p>
        </div>
      ) : (
        <div className="rounded-3xl bg-card border border-white/5 overflow-hidden">
          {items.map((r, i) => (
            <motion.div
              key={r.record_id}
              initial={{ x: -10, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: Math.min(i * 0.03, 0.6) }}
              className="flex items-center gap-3 p-3 border-b border-white/5 last:border-0"
              data-testid="record-row"
            >
              <span className="text-2xl">{EMOJI[r.scope] || '🏆'}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{r.species_common_name}</p>
                <p className="text-[10px] text-muted-foreground truncate">
                  {formatScope(r)} · <span className="text-accent">{r.user_name || 'Explorer'}</span>
                </p>
              </div>
              <span className="text-[10px] font-mono text-muted-foreground">
                {new Date(r.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </span>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatScope(r) {
  if (r.scope === 'world') return 'World First';
  if (r.scope === 'region') return `Regional First — ${r.scope_value || r.area || ''}`;
  if (r.scope === 'season') return `${r.scope_value.split('-')[0]} First`;
  if (r.scope === 'month') return `Monthly First — ${r.scope_value}`;
  return r.badge;
}

function Metric({ label, value }) {
  return (
    <div className="rounded-2xl bg-card border border-white/5 p-3 text-center">
      <p className="font-mono font-semibold text-2xl tabular-nums">{value}</p>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground mt-1">{label}</p>
    </div>
  );
}
